import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import AISecurityFindingPopover from './AISecurityFindingPopover';
import FastValueViewer from '../../../../../components/ScalarSpreadsheet/utilities/FastValueViewer';
import { AIFieldMetadata } from '../captable-table/CaptableTable.props';

// 24x24 sparkle SVG cursor using Scalar AI gradient
const AI_CURSOR_SVG = encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">'
  + '<defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1">'
  + '<stop offset="0%" stop-color="#4F55E7"/>'
  + '<stop offset="40%" stop-color="#6E46EC"/>'
  + '<stop offset="80%" stop-color="#D213DF"/>'
  + '<stop offset="100%" stop-color="#00AEC6"/>'
  + '</linearGradient></defs>'
  + '<path d="M12 1l2.5 7.5L22 12l-7.5 2.5L12 22l-2.5-7.5L2 12l7.5-2.5z" fill="url(#g)"/>'
  + '</svg>'
);
const AI_CURSOR = `url("data:image/svg+xml,${AI_CURSOR_SVG}") 12 12, pointer`;

interface AIFindingValueViewerProps {
  value: string | number;
  aiFieldMetadata: AIFieldMetadata;
  aiAccuracyConfidence?: string | null;
  fieldLabel: string;
  onRemoveFinding?: () => void;
  originalValueViewer?: React.ComponentType<any>;
  [key: string]: any;
}

const AIFindingValueViewer = ({
  value,
  aiFieldMetadata,
  aiAccuracyConfidence,
  fieldLabel,
  onRemoveFinding,
  originalValueViewer: OriginalValueViewer,
  ...restProps
}: AIFindingValueViewerProps) => {
  const [ctrlHeld, setCtrlHeld] = useState(false);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey) setCtrlHeld(true);
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (!e.ctrlKey && !e.metaKey) setCtrlHeld(false);
    };
    const onBlur = () => setCtrlHeld(false);

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', onBlur);
    };
  }, []);

  return (
    <AISecurityFindingPopover
      fieldMetadata={aiFieldMetadata}
      accuracyConfidence={aiAccuracyConfidence}
      fieldLabel={fieldLabel}
      onRemove={onRemoveFinding}
    >
      {({ onClick }) => (
        <Box
          /*
          If Ctrl/Meta was held during the click, prevent default + stop propagation
          to the children and thus let the onClickCapture below open the AI popover.
          */
          onMouseDownCapture={(e) => {
            if (e.ctrlKey || e.metaKey) {
              e.preventDefault();
              e.stopPropagation();
            }
          }}
          onClickCapture={onClick}
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            overflow: 'hidden',
            ...(ctrlHeld && {
              cursor: AI_CURSOR,
              '& *': { cursor: `${AI_CURSOR} !important` },
            }),
          }}
        >
          {OriginalValueViewer ? (
            <OriginalValueViewer value={value} {...restProps} />
          ) : (
            <FastValueViewer value={value} {...restProps} />
          )}
        </Box>
      )}
    </AISecurityFindingPopover>
  );
};

export default AIFindingValueViewer;
